// "Fit-to-content" zoom (Shift+0): find the smallest vertical zoom at which every
// rendered event card shows its full content without vertical clipping.
//
// The target zoom is derived from each card's natural content height divided by its
// rendered span in minutes — never by scaling measured card heights, because the
// grid's minmax(..., 1fr) rows STRETCH when the day is shorter than the viewport,
// so a card's current height is not proportional to zoom.
//
// Not every box participates: one degenerate card (a decorative slot, a stub clipped
// by the visible-hours window, or a micro-appointment whose content can't fit even at
// max zoom) would otherwise drag the whole day to the maximum and leave every normal
// card several times taller than its content. Those cards keep their inner scroll.

import {
  BASE_SLOT_REM,
  BASE_SLOT_MINUTES,
  CALENDAR_FINE_MINUTES,
} from "./calendar-time-axis";

// Fit never targets beyond this, even though manual zoom goes to 1200%: past ~600%
// an hour is taller than most screens and the day stops being navigable, so letting
// one dense micro-appointment demand 1200% would trade "one box readable" for "the
// whole day absurd". Boxes needing more keep their inner scroll.
const FIT_CEILING_PERCENT = 600;

/**
 * Value for a card's `data-span-minutes` attribute (which opts the card into
 * fit-zoom measurement), or null when the card must not participate:
 *   • slots — their content is decorative, and their short spans would dominate
 *     the fit while nobody needs to "read" them;
 *   • events clipped by the visible-hours window — the rendered stub can never
 *     meaningfully show the full appointment's content at any zoom.
 */
export function fitSpanMinutes(event, position) {
  if (event.kind === "slot") return null;
  const spanMinutes = position.span * CALENDAR_FINE_MINUTES;
  const durationMinutes = Math.round(event.endAt.diff(event.startAt, "minutes").minutes);
  if (spanMinutes < durationMinutes) return null;
  return spanMinutes;
}

/**
 * Smallest vertical zoom percent at which every participating event card inside
 * `container` shows its full content, clamped to [minPercent, maxPercent] and to
 * the fit ceiling. Cards whose content cannot fit under the ceiling are ignored
 * (satisfying them would inflate every other box several-fold); if NO card can
 * fit, returns the ceiling as the best effort. Returns null when no measurable
 * cards are rendered (caller should no-op).
 */
export function computeFitZoomPercent({ container, minPercent, maxPercent }) {
  if (!container) return null;
  const cells = container.querySelectorAll("li[data-span-minutes]");
  if (cells.length === 0) return null;

  const fitMax = Math.min(maxPercent, FIT_CEILING_PERCENT);

  const rootFontSize =
    Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
  const basePxPerMinuteAt100 = (BASE_SLOT_REM * rootFontSize) / BASE_SLOT_MINUTES;

  let sawCard = false;
  let required = null;
  for (const cell of cells) {
    const spanMinutes = Number(cell.dataset.spanMinutes);
    if (!Number.isFinite(spanMinutes) || spanMinutes <= 0) continue;
    const card = cell.querySelector(":scope > [data-calendar-event-card]");
    if (!card) continue;
    const cardRect = card.getBoundingClientRect();
    if (cardRect.height <= 0 || cardRect.width <= 0) continue; // breakpoint-hidden column
    const viewport = card.querySelector("[data-radix-scroll-area-viewport]");
    const contentEl = viewport?.firstElementChild;
    if (!contentEl) continue;
    // Natural content height = the auto-height ScrollArea content wrapper. NOT
    // viewport.scrollHeight — that floors at clientHeight, which would block
    // zooming OUT. Width (and thus wrapping/truncation) is unaffected by vertical
    // zoom, so this measurement stays valid at the target zoom.
    const contentPx = contentEl.getBoundingClientRect().height;
    // Card chrome around the scroll viewport (borders, the cancelled-variant wrapper).
    const chromePx = Math.max(cardRect.height - viewport.getBoundingClientRect().height, 0);
    // Grid-track overhead outside the card: its top/bottom insets within the <li>
    // plus the <li>'s own margin. Measured live so it survives styling changes.
    const insetPx =
      Math.max(cell.getBoundingClientRect().height - cardRect.height, 0) +
      (Number.parseFloat(window.getComputedStyle(cell).marginTop) || 0);
    const zoomNeeded = ((contentPx + chromePx + insetPx) / spanMinutes / basePxPerMinuteAt100) * 100;
    sawCard = true;
    if (zoomNeeded > fitMax) continue; // unfittable outlier — keep its inner scroll
    required = required == null ? zoomNeeded : Math.max(required, zoomNeeded);
  }

  if (!sawCard) return null;
  if (required == null) return fitMax; // nothing fits under the ceiling — best effort
  return Math.min(fitMax, Math.max(minPercent, Math.ceil(required)));
}
